import { AnimalContractDetails } from 'api/contract';
import { CardsSection } from 'components/layout/Section/CardsSection';
import { GenericLoading } from 'components/ui/Loader';
import { Bone, IdCard, ShieldCheck } from 'lucide-react';
import { FC, useEffect, useState } from 'react';
import { contractsAPI } from 'services/swagger-api';
import { useContractInfosState } from '../ContractInfosContext';

const SPECIES_LABELS: Record<string, string> = {
  CHIEN: 'Chien',
  CHAT: 'Chat',
};

const formatDate = (date?: Date | null): string => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const computeAge = (birthDate: Date): string => {
  const today = new Date();
  let years = today.getFullYear() - birthDate.getFullYear();
  let months = today.getMonth() - birthDate.getMonth();
  if (today.getDate() < birthDate.getDate()) months -= 1;
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  if (years <= 0 && months <= 0) return 'Moins d\'un mois';
  if (years <= 0) return `${months} mois`;
  if (months === 0) return `${years} an${years > 1 ? 's' : ''}`;
  return `${years} an${years > 1 ? 's' : ''} et ${months} mois`;
};

interface InfoItemProps {
  label: string;
  value: string;
}

const InfoItem: FC<InfoItemProps> = ({ label, value }) => (
  <div>
    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">{label}</p>
    <p className="text-sm font-semibold text-slate-800 mt-0.5">{value}</p>
  </div>
);

const YesNoBadge: FC<{ value: boolean; positiveIsGood?: boolean }> = ({ value, positiveIsGood = true }) => {
  const isGreen = positiveIsGood ? value : !value;
  return (
    <span
      className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full ${
        isGreen ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
      }`}
    >
      {value ? 'Oui' : 'Non'}
    </span>
  );
};

export const ContractPetOverview: FC = () => {
  const { contractId } = useContractInfosState();
  const [data, setData] = useState<AnimalContractDetails>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAnimal = async () => {
      try {
        setIsLoading(true);
        const response = await contractsAPI.getAnimalContractDetails({ contractId });
        if (response) setData(response);
      } catch (error) {
        console.error('Erreur lors de la récupération des détails animal:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAnimal();
  }, [contractId]);

  if (isLoading || !data) {
    return (
      <div className="relative flex justify-center items-center p-8">
        <GenericLoading isParentSized />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <CardsSection title="Identité" icon={<Bone size={18} className="text-primary" />}>
        <div className="p-4 rounded-xl bg-white border border-slate-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <InfoItem label="Nom" value={data.name || '-'} />
            <InfoItem label="Espèce" value={SPECIES_LABELS[data.speciesCode] || data.speciesCode || '-'} />
            <InfoItem label="Race" value={data.breedCode || '-'} />
            <InfoItem label="Date de naissance" value={formatDate(data.birthDate)} />
            <InfoItem label="Âge" value={computeAge(data.birthDate)} />
          </div>
        </div>
      </CardsSection>

      <CardsSection title="Identification" icon={<IdCard size={18} className="text-primary" />}>
        <div className="p-4 rounded-xl bg-white border border-slate-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-center">
            <div>
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Identifié</p>
              <div className="mt-1">
                <YesNoBadge value={data.isIdentified} />
              </div>
            </div>
            <InfoItem label="Numéro d'identification" value={data.identificationNumber || '-'} />
          </div>
        </div>
      </CardsSection>

      <CardsSection title="Santé & statut" icon={<ShieldCheck size={18} className="text-primary" />}>
        <div className="p-4 rounded-xl bg-white border border-slate-200">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Vaccins à jour</p>
              <div className="mt-1">
                <YesNoBadge value={data.vaccinesUpToDate} />
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Usage professionnel</p>
              <div className="mt-1">
                <YesNoBadge value={data.isProfessionalUse} positiveIsGood={false} />
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Chien catégorie 1 ou 2</p>
              <div className="mt-1">
                <YesNoBadge value={data.isDogCategory1Or2} positiveIsGood={false} />
              </div>
            </div>
          </div>
        </div>
      </CardsSection>
    </div>
  );
};
